import { useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '../lib/auth.jsx'
import { toast } from 'sonner'
import { RiLoaderLine, RiSettings3Line } from 'react-icons/ri'

/** Red asterisk for required inputs */
function Req() {
  return <span className="ml-0.5 text-red-500" aria-hidden>*</span>
}

export default function LoginPage() {
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const [loginVal, setLoginVal] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  if (user) return <Navigate to="/" replace />

  const onSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      await login(loginVal, password)
      toast.success('Selamat datang kembali!')
      navigate('/')
    } catch (err) {
      const msg = err.response?.data?.message || ''
      if (msg.toLowerCase().includes('disabled') || msg.toLowerCase().includes('nonaktif')) {
        setError('Akun Anda telah dinonaktifkan. Hubungi administrator.')
      } else if (err.response?.status === 422) {
        // Backend returns validation error for wrong credentials
        const errData = err.response?.data?.errors
        const firstMsg = errData?.login?.[0] || errData?.password?.[0] || msg
        setError(firstMsg || 'Email/nama pengguna atau kata sandi salah.')
      } else {
        setError('Login gagal. Silakan coba lagi.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen bg-slate-900">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-start justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500 shadow-lg">
            <RiSettings3Line className="text-white text-base" />
          </div>
          <span className="text-lg font-semibold text-white">LPK CMS</span>
        </div>
        <div className="max-w-sm">
          <h2 className="text-3xl font-bold text-white leading-snug">
            Kelola konten website
            <span className="text-blue-400"> dengan mudah.</span>
          </h2>
          <p className="mt-4 text-slate-400 text-sm leading-relaxed">
            Upload gambar, kelola kelas, dan pantau tampilan landing page secara real-time melalui panel admin ini.
          </p>
        </div>
        <p className="text-xs text-slate-600">© 2025 LPK Asa Hikari Mulya</p>
      </div>

      {/* Right panel */}
      <div className="flex flex-1 items-center justify-center bg-white p-8 lg:rounded-l-3xl">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500">
              <RiSettings3Line className="text-white text-sm" />
            </div>
            <span className="text-sm font-semibold text-gray-900">LPK CMS</span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900">Masuk</h1>
          <p className="mt-1 text-sm text-gray-500">
            Masuk menggunakan email atau nama pengguna
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                Email / Nama Pengguna<Req />
              </label>
              <input
                type="text"
                required
                value={loginVal}
                onChange={(e) => setLoginVal(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-shadow"
                autoComplete="username"
                placeholder="contoh@email.com atau nama"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                Kata Sandi<Req />
              </label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-shadow"
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <span className="mt-0.5 flex-shrink-0 font-bold">!</span>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting && <RiLoaderLine className="animate-spin text-base" />}
              {submitting ? 'Memproses…' : 'Masuk'}
            </button>
          </form>

          <p className="mt-8 text-xs text-gray-400 text-center">
            Khusus pengelola konten resmi LPK Asa Hikari Mulya.
          </p>
        </div>
      </div>
    </div>
  )
}
